import DOMPurify from 'dompurify';

/**
 * Sanitizes an HTML string using DOMPurify so it is safe to render via
 * dangerouslySetInnerHTML. Only the tags and attributes produced by the
 * admin rich-text editor are allowed; everything else is stripped.
 */
const DEFAULT_ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "u",
  "h1",
  "h2",
  "h3",
  "h4",
  "ul",
  "ol",
  "li",
  "a",
  "img",
  "blockquote",
  "span",
  "div",
];

const DEFAULT_ALLOWED_ATTR = [
  "href",
  "src",
  "alt",
  "title",
  "target",
  "rel",
  "class",
  "style"
];

export const sanitizeHtml = (
  html: string,
  options?: {
    allowedTags?: string[];
    allowedAttrs?: string[];
  }
): string => {
  if (!html) return "";

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: options?.allowedTags || DEFAULT_ALLOWED_TAGS,
    ALLOWED_ATTR: options?.allowedAttrs || DEFAULT_ALLOWED_ATTR,
    FORBID_TAGS: ["script", "style", "iframe", "object", "embed"],
    KEEP_CONTENT: true,
    USE_PROFILES: { html: true },
  });
};


/**
 * Returns a payload suitable for dangerouslySetInnerHTML.
 * Works for plain text and html strings.
 */
export const parseHtmlString = (html: string): { __html: string } => ({
  __html: sanitizeHtml(html),
});

/**
 * Strips all HTML tags and collapses whitespace, returning plain text.
 * Useful for search comparisons, meta descriptions, etc.
 */
export const stripHtml = (html: string): string =>
  html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
